import { useEffect, useState } from "react";

import {
  B2,
  B3,
  B4,
  B5,
  Box,
  Button,
  P,
  Spinner,
  Stack,
  Text
} from "@adfenix/alexandria";
import UilLock from "@iconscout/react-unicons/icons/uil-lock";
import { useRecoilValue } from "recoil";
import { userSelectedHq } from "atoms/auth";
import Page from "components/Page";
import Card from "components/Card";
import PageHeader from "components/PageHeader";
import { HqStripeCardHandler } from "components/StripeComponent/HQ/HqStripeCardHandler";
import Divider from "components/Divider";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { InvoiceHistory } from "./InvoiceHistory";
import { getHqInvoiceInfo } from "api/services/billingService";
import { getHqStripePaymentMethods } from "api/services/stripeService";
import { StripeTransactionHistory } from "./TransactionHistory/StripeTransactionHistory";

export const HqBillingPage = () => {
  const selectedHq = useRecoilValue(userSelectedHq);
  const { t } = useTranslation();

  const [selectedHqId, setSelectedHqId] = useState<string | undefined>(
    undefined
  );

  const {
    isInitialLoading: loadingCardsInfo,
    data: creditCardsInfo,
    refetch: updateCardsInfo
  } = useQuery(
    ["getHqStripePaymentMethods", selectedHqId],
    () => getHqStripePaymentMethods(selectedHqId!),
    {
      enabled: Boolean(selectedHqId),
      retry: false
    }
  );

  const { data: invoiceInfo } = useQuery(
    ["getHqInvoiceInfo", selectedHqId],
    () => getHqInvoiceInfo(selectedHqId!),

    {
      enabled: Boolean(selectedHqId),

      retry: false
    }
  );

  const [showAddCreditCardComponent, setShowAddCreditCardComponent] =
    useState(false);

  useEffect(() => {
    if (selectedHq?.id) {
      setSelectedHqId(selectedHq.id);
    }
  }, [selectedHq]);

  useEffect(() => {
    if (creditCardsInfo !== undefined && creditCardsInfo?.length) {
      setShowAddCreditCardComponent(true);
    }
  }, [creditCardsInfo]);

  if (loadingCardsInfo) return <Spinner centered />;

  const isCardListEmpty =
    creditCardsInfo === undefined || !creditCardsInfo?.length;

  return (
    <Page
      header={
        <Box>
          <PageHeader title={t("billing_hq_page_title")} />

          <Text>
            {t("billing_page_sub_title")}{" "}
            <B5 color="grey">{selectedHq?.descriptiveName}</B5>{" "}
          </Text>
        </Box>
      }
    >
      <Stack spacing="s">
        <B2>{t("billing_page_payment_preferences_title")}</B2>
        <P>{t("billing_page_hq_payment_preferences_sub_title")}</P>
      </Stack>

      <Stack spacing="gutter" pY="xl2">
        <Card>
          <B3>{t("billing_invoice_card_title")}</B3>

          <Box pY="xs">
            <P>
              {t("billing_invoice_card_email")}{" "}
              {invoiceInfo?.email ?? t("billing_invoice_card_email_not_found")}
            </P>
          </Box>
        </Card>

        <Card>
          <Stack spacing="m">
            <B3>{t("billing_credit_card_card_title")}</B3>
            <P>
              {isCardListEmpty
                ? t("billing_empty_credit_card_hq_sub_title")
                : t("billing_credit_card_hq_sub_title")}
            </P>

            {selectedHqId && showAddCreditCardComponent && (
              <>
                <Stack spacing="xs">
                  <HqStripeCardHandler
                    loadingCardInfo={loadingCardsInfo}
                    updateCardInfo={updateCardsInfo}
                    creditCardsInfo={creditCardsInfo}
                    isCardListEmpty={isCardListEmpty}
                    handleCancel={() => setShowAddCreditCardComponent(false)}
                    hqId={selectedHqId!}
                  />
                </Stack>
              </>
            )}

            {selectedHqId && isCardListEmpty && !showAddCreditCardComponent ? (
              <Box pY="l">
                <Button
                  logLabel="hq-add-card-button"
                  onClick={() => setShowAddCreditCardComponent(true)}
                >
                  {t("billing_add_card_card_button_title")}
                </Button>
              </Box>
            ) : (
              <Box display="flex" alignItems="center">
                <UilLock size="16px" />
                <Text>{t("billing_stripe_encrypted_transactions")}</Text>&nbsp;
                <a
                  href="https://stripe.com"
                  target="_blank"
                  rel="noreferrer"
                  data-loglabel="stripe-link"
                >
                  Stripe
                </a>
              </Box>
            )}
          </Stack>
        </Card>
      </Stack>

      <Divider margin="40px 0" />

      <B2>{t("billing_history_title")}</B2>

      <Stack spacing="xl4" pY="s">
        <>
          <B4>{t("billing_invoice_history_card_title")}</B4>
          <Box pY="xs2">
            <InvoiceHistory hqId={selectedHqId} mode="hq" />
          </Box>
        </>

        <>
          <B4>{t("billing_credit_card_history_card_title")}</B4>

          <Box pY="xs2">
            {selectedHqId && (
              <StripeTransactionHistory hqId={selectedHqId} mode="hq" />
            )}
          </Box>
        </>
      </Stack>
    </Page>
  );
};
